/* =========================================================================== *\
 Backup history (backup_system.logs_erp_mariadb)
\* =========================================================================== */

import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import axios from 'axios';

import Card from '@mui/material/Card';
import Icon from '@mui/material/Icon';
import Menu from '@mui/material/Menu';
import MenuItem from '@mui/material/MenuItem';
import Backdrop from '@mui/material/Backdrop';
import CircularProgress from '@mui/material/CircularProgress';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableRow from '@mui/material/TableRow';
import Paper from '@mui/material/Paper';
import Pagination from '@mui/material/Pagination';
import Grid from '@mui/material/Grid';
import InputAdornment from '@mui/material/InputAdornment';
import Tooltip from '@mui/material/Tooltip';
import IconButton from '@mui/material/IconButton';
import Chip from '@mui/material/Chip';

import MDBox from 'components/MDBox';
import MDButton from 'components/MDButton';
import MDInput from 'components/MDInput';
import MDSnackbar from 'components/MDSnackbar';
import TableHeader from 'components/Table/TableHeader';
import MDDateRange from 'components/MDDateRange';
import MDSelect from 'components/MDSelect';

import DashboardLayout from 'examples/LayoutContainers/DashboardLayout';
import Navbar from 'examples/Navbars/NavBar';
import Footer from 'examples/Footer';
import { APP_STATE, API } from 'services/Define';
import { FetchData } from 'services/Request';
import * as Func from 'services/Function';

const initFind = {
  search: '',
  order_by: 'tb.id',
  order_sort: 'desc',
  page: 1,
  perPage: 10,
  pageTotal: 0,
  period: 'ALL',
  status: 'ALL',
  database_name: 'ALL',
  date_start: Func.dateTimeAdd(-30),
  date_stop: Func.dateTimeNow(),
};

function Backup() {
  const mod = 'backup';
  const app = useSelector((state) => state[APP_STATE]);
  const { t, i18n } = useTranslation();
  const [pageTotal, setPageTotal] = useState(0);
  const [pageNo, setPageNo] = useState(1);
  const [record, setRecord] = useState([]);
  const [show, setShow] = useState(null);
  const [find, setFind] = useState(initFind);
  const [notify, setNotify] = useState(null);
  const [loading, setLoading] = useState(true);
  const [downloadingId, setDownloadingId] = useState(null);
  const [order, setOrder] = useState(initFind.order_sort);
  const [orderBy, setOrderBy] = useState(initFind.order_by);
  const [cboDatabase, setCboDatabase] = useState([]);
  const [cboStatus, setCboStatus] = useState([]);

  const openShow = (e) => setShow(e.currentTarget);
  const closeShow = () => setShow(null);

  const initHeader = [
    { id: 'tb.id', label: t('attr.id'), align: 'center', width: 60 },
    { id: 'tb.database_name', label: 'Database', align: 'left' },
    { id: 'tb.file_name', label: 'ชื่อไฟล์', align: 'left' },
    { id: 'tb.file_size_mb', label: 'ขนาด (MB)', align: 'right' },
    { id: 'tb.status', label: 'สถานะ', align: 'center' },
    { id: 'tb.created_at', label: t('attr.created_at'), align: 'center' },
    { id: 'tb.expire_at', label: 'หมดอายุ', align: 'center' },
    { id: 'download', label: '', align: 'center', sort: false, width: 50 },
  ];

  const showNotify = (msg, color = 'error') => {
    setNotify(
      <MDSnackbar
        color={color}
        icon='notifications'
        title={t('menu.backup')}
        content={msg}
        dateTime={Func.dateTimeDisplay()}
        open
        close={() => setNotify(null)}
      />
    );
    setTimeout(() => setNotify(null), 4000);
  };

  const init = () => {
    FetchData('GET', `/${mod}`, app.token, {}).then((result) => {
      if (result.status !== 'ok') {
        showNotify(result.msg || 'โหลดข้อมูลไม่สำเร็จ');
      }
    });
  };

  const view = (data) => {
    setFind(data);
    setLoading(true);
    setRecord([]);
    closeShow();

    FetchData('POST', `/${mod}`, app.token, {
      lang: app.lang,
      search: data.search,
      page: data.page,
      per_page: data.perPage,
      orderby: data.order_by,
      ordersort: data.order_sort,
      period: data.period,
      status: data.status,
      database_name: data.database_name,
      date_start: data.date_start,
      date_stop: data.date_stop,
    }).then((result) => {
      if (result.status === 'ok') {
        setRecord(result.data);
        setPageTotal(result.total_pages);
        setPageNo(result.page);
        if (result.database) setCboDatabase(result.database);
        if (result.status_list) setCboStatus(result.status_list);
      } else {
        showNotify(result.msg || 'โหลดข้อมูลไม่สำเร็จ');
      }
      setLoading(false);
    });
  };

  const handleSort = (e, property) => {
    const isAsc = orderBy === property && order === 'asc';
    setOrder(isAsc ? 'desc' : 'asc');
    setOrderBy(property);
    view({ ...find, order_by: property, order_sort: isAsc ? 'desc' : 'asc', page: 1 });
  };

  const getFileNameFromPath = (filePath) => {
    if (!filePath) return '';
    const normalized = String(filePath).replace(/\\/g, '/');
    const parts = normalized.split('/');
    return parts[parts.length - 1] || '';
  };

  const downloadFile = async (row) => {
    if (!row.file_path) {
      showNotify('ไม่พบ path ของไฟล์');
      return;
    }

    setDownloadingId(row.id);

    const fileName = getFileNameFromPath(row.file_path) || `backup_${row.id}`;

    try {
      const res = await axios({
        url: `${API}/backup-download/${row.id}`,
        method: 'GET',
        headers: { Authorization: `Bearer ${app.token}` },
        responseType: 'blob',
      });

      const url = window.URL.createObjectURL(new Blob([res.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', fileName);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (err) {
      showNotify('ดาวน์โหลดไม่สำเร็จ');
    } finally {
      setDownloadingId(null);
    }
  };

  const setDateRange = (start, stop) => {
    view({
      ...find,
      date_start: Func.dateTimeFormat(start.toDate()),
      date_stop: Func.dateTimeFormat(stop.toDate()),
      page: 1,
    });
  };

  const renderStatus = (status) => {
    if (!status) return '-';
    const color =
      status === 'success' || status === 'completed'
        ? 'success'
        : status === 'failed' || status === 'error'
        ? 'error'
        : 'default';
    return <Chip label={status} color={color} size='small' />;
  };

  const renderLoading = () => (
    <Backdrop sx={{ color: '#fff', zIndex: (theme) => theme.zIndex.drawer + 1 }} open={loading}>
      <CircularProgress color='inherit' />
    </Backdrop>
  );

  const renderShow = (
    <Menu
      anchorEl={show}
      anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
      transformOrigin={{ vertical: 'top', horizontal: 'left' }}
      open={Boolean(show)}
      onClose={closeShow}
      keepMounted
    >
      <MenuItem onClick={() => view({ ...find, page: 1, perPage: 10 })}>
        {t('element.show_10_record')}
      </MenuItem>
      <MenuItem onClick={() => view({ ...find, page: 1, perPage: 20 })}>
        {t('element.show_20_record')}
      </MenuItem>
      <MenuItem onClick={() => view({ ...find, page: 1, perPage: 100 })}>
        {t('element.show_100_record')}
      </MenuItem>
    </Menu>
  );

  const renderList = () => (
    <MDBox pt={2} pb={3}>
      <Grid container spacing={3}>
        <Grid item xs={12}>
          <Card>
            <MDBox p={2}>
              <Grid container spacing={2} alignItems='center'>
                <Grid item xs={12} md={3}>
                  <MDInput
                    fullWidth
                    value={find.search}
                    placeholder={t('element.search')}
                    onChange={(e) => setFind({ ...find, search: e.target.value })}
                    onKeyDown={(e) => e.key === 'Enter' && view({ ...find, page: 1 })}
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position='start'>
                          <Icon>search</Icon>
                        </InputAdornment>
                      ),
                    }}
                  />
                </Grid>
                <Grid item xs={12} md={2}>
                  <MDSelect
                    label='Database'
                    labelDefault='ทั้งหมด'
                    valueDefault='ALL'
                    data={cboDatabase}
                    value={find.database_name}
                    onChange={(e) => view({ ...find, database_name: e.target.value, page: 1 })}
                  />
                </Grid>
                <Grid item xs={12} md={2}>
                  <MDSelect
                    label='สถานะ'
                    labelDefault='ทั้งหมด'
                    valueDefault='ALL'
                    data={cboStatus}
                    value={find.status}
                    onChange={(e) => view({ ...find, status: e.target.value, page: 1 })}
                  />
                </Grid>
                <Grid item xs={12} md={2}>
                  {find.period === 'ALL' ? (
                    <MDButton
                      variant='outlined'
                      color='secondary'
                      fullWidth
                      sx={{ p: 1.5 }}
                      onClick={() => view({ ...find, period: 'DATE', page: 1 })}
                    >
                      <Icon>edit_calendar</Icon>&nbsp;เลือกช่วงเวลา
                    </MDButton>
                  ) : (
                    <MDDateRange
                      dateStart={find.date_start}
                      dateStop={find.date_stop}
                      callBack={setDateRange}
                      closeDate={() => view({ ...find, period: 'ALL', page: 1 })}
                    />
                  )}
                </Grid>
                <Grid item xs={12} md={2}>
                  <MDButton
                    variant='gradient'
                    color='info'
                    fullWidth
                    onClick={() => view({ ...find, page: 1 })}
                  >
                    <Icon>search</Icon>&nbsp;ค้นหา
                  </MDButton>
                </Grid>
              </Grid>
            </MDBox>
          </Card>
        </Grid>
        <Grid item xs={12}>
          <Card>
            <TableContainer component={Paper} sx={{ boxShadow: 0 }}>
              <Table>
                <TableHeader
                  data={initHeader}
                  order={order}
                  orderBy={orderBy}
                  onRequestSort={handleSort}
                />
                <TableBody>
                  {record.map((row) => (
                    <TableRow key={row.id} hover>
                      <TableCell align='center'>{row.id}</TableCell>
                      <TableCell>{row.database_name || '-'}</TableCell>
                      <TableCell>{row.file_name || '-'}</TableCell>
                      <TableCell align='right'>
                        {row.file_size_mb != null ? Func.numberDisplay(row.file_size_mb, 2) : '-'}
                      </TableCell>
                      <TableCell align='center'>{renderStatus(row.status)}</TableCell>
                      <TableCell align='center'>{Func.dateTimeDisplay(row.created_at)}</TableCell>
                      <TableCell align='center'>{Func.dateTimeDisplay(row.expire_at)}</TableCell>
                      <TableCell align='center'>
                        <Tooltip title='ดาวน์โหลด Backup'>
                          <span>
                            <IconButton
                              color='info'
                              size='small'
                              disabled={downloadingId === row.id || !row.file_path}
                              onClick={() => downloadFile(row)}
                            >
                              {downloadingId === row.id ? (
                                <CircularProgress size={20} />
                              ) : (
                                <Icon>file_download</Icon>
                              )}
                            </IconButton>
                          </span>
                        </Tooltip>
                      </TableCell>
                    </TableRow>
                  ))}
                  {record.length === 0 && !loading && (
                    <TableRow>
                      <TableCell colSpan={8} align='center'>
                        ไม่พบข้อมูล
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          </Card>
        </Grid>
        <Grid item xs={12}>
          <Card sx={{ p: 2 }}>
            <MDBox display='flex' justifyContent='space-between' alignItems='flex-start'>
              <MDButton variant={show ? 'contained' : 'outlined'} color='dark' onClick={openShow}>
                {t('element.show_record', { num: find.perPage })}&nbsp;
                <Icon>keyboard_arrow_down</Icon>
              </MDButton>
              {renderShow}
              <Pagination
                count={pageTotal}
                variant='outlined'
                color='secondary'
                showFirstButton
                showLastButton
                page={pageNo}
                onChange={(e, p) => view({ ...find, page: p })}
              />
            </MDBox>
          </Card>
        </Grid>
      </Grid>
    </MDBox>
  );

  useEffect(() => {
    i18n.changeLanguage(app.lang);
    init();
    view(initFind);
  }, []);

  return (
    <DashboardLayout>
      <Navbar route={[t('menu.setting'), t('menu.backup')]} />
      {renderList()}
      <Footer />
      {renderLoading()}
      {notify}
    </DashboardLayout>
  );
}

export default Backup;
